import { useCallback, useMemo, useState } from "react"
import { Badge, Button, Card, CardBody, UncontrolledTooltip } from "reactstrap"
import { toast } from "react-toastify"

import SectionHeader from "components/Common/SectionHeader"
import ThemeModal from "components/Common/ThemeModal"
import ThemePlaceholder from "components/Common/ThemePlaceholder"
import { CanView } from "components/Common/CanView"
import { ACCESS } from "constants/accessPermissions"

import {
  useCreateVehicleImporterBankAccount,
  useDeleteVehicleImporterBankAccount,
  useEditVehicleImporterBankAccount,
  useSetDefaultVehicleImporterBankAccount,
} from "hooks/useVehicleImporterBankAccount"

import ImporterBankAccountAddEdit from "./ImporterBankAccountAddEdit"

const hasValue = value => {
  if (value === null || value === undefined) return false
  if (typeof value === "string" && value.trim() === "") return false
  return true
}

const maskAccount = number => {
  if (!hasValue(number)) return "-"
  const str = String(number)
  if (str.length <= 4) return str
  return `•••• ${str.slice(-4)}`
}

const AccountItem = ({ account, index, onEdit, onDelete, onSetDefault }) => {
  const hasAccounts = true
  return (
    <div className="mb-3 pb-3 border-bottom">
      <div className="d-flex justify-content-between align-items-start mb-2">
        <div>
          <strong className="d-block">
            {hasValue(account.label) ? account.label : (
              <Badge color="light" className="text-muted">Unlabeled Account</Badge>
            )}
          </strong>
          <small className="text-muted d-block">
            {account.bank_name || "-"}
            {hasValue(account.branch_name) ? ` — ${account.branch_name}` : ""}
          </small>
        </div>
        <div className="d-flex align-items-center gap-1">
          {account.is_default ? (
            <Badge color="success" pill>Default</Badge>
          ) : null}
          {!account.is_active ? (
            <Badge color="secondary" pill>Inactive</Badge>
          ) : null}
          <Badge color="light">#{index + 1}</Badge>
        </div>
      </div>

      <div className="specs-list">
        <div className="d-flex justify-content-between py-1">
          <span className="text-muted">Account Name</span>
          <span className="fw-medium text-end">{account.account_name || "-"}</span>
        </div>
        <div className="d-flex justify-content-between py-1">
          <span className="text-muted">Account Number</span>
          <span className="fw-medium font-monospace text-end">
            {maskAccount(account.account_number)}
          </span>
        </div>
        {hasValue(account.routing_number) ? (
          <div className="d-flex justify-content-between py-1">
            <span className="text-muted">Routing</span>
            <span className="fw-medium font-monospace text-end">
              {account.routing_number}
            </span>
          </div>
        ) : null}
        {hasValue(account.account_mobile_number) ? (
          <div className="d-flex justify-content-between py-1">
            <span className="text-muted">Mobile</span>
            <span className="fw-medium text-end">
              <i className="bx bx-phone me-1 text-muted" style={{ fontSize: "14px" }}></i>
              {account.account_mobile_number}
            </span>
          </div>
        ) : null}
      </div>

      <div className="d-flex gap-2 mt-2 flex-wrap">
        <CanView access={[ACCESS.IMPORTER_BANK_ACCOUNT.EDIT]}>
          {!account.is_default ? (
            <Button
              color="success"
              size="sm"
              outline
              id={`set-default-importer-bank-${account.id}`}
              onClick={() => onSetDefault(account)}
            >
              <i className="bx bx-check-double me-1" />
              Set Default
            </Button>
          ) : null}
          <Button
            color="info"
            size="sm"
            id={`edit-importer-bank-${account.id}`}
            onClick={() => onEdit(account)}
          >
            <i className="mdi mdi-pencil-outline me-1" />
            Edit
          </Button>
        </CanView>
        <CanView access={[ACCESS.IMPORTER_BANK_ACCOUNT.DELETE]}>
          <Button
            color="danger"
            size="sm"
            outline
            id={`delete-importer-bank-${account.id}`}
            onClick={() => onDelete(account)}
          >
            <i className="mdi mdi-delete-outline me-1" />
            Delete
          </Button>
        </CanView>
      </div>

      {!account.is_default ? (
        <UncontrolledTooltip placement="top" target={`set-default-importer-bank-${account.id}`}>
          Mark as default
        </UncontrolledTooltip>
      ) : null}
      <UncontrolledTooltip placement="top" target={`edit-importer-bank-${account.id}`}>
        Edit bank account
      </UncontrolledTooltip>
      <UncontrolledTooltip placement="top" target={`delete-importer-bank-${account.id}`}>
        Delete bank account
      </UncontrolledTooltip>
    </div>
  )
}

const ConfirmDeleteModal = ({ modal, toggle, account, onConfirm, loading }) => {
  return (
    <ThemeModal
      isOpen={modal}
      toggle={toggle}
      title="Delete Bank Account"
      size="sm"
    >
      <p className="mb-3">
        Are you sure you want to delete{" "}
        <strong>
          {account?.label || account?.bank_name || "this bank account"}
        </strong>
        ? This action cannot be undone.
      </p>
      <div className="text-end">
        <Button color="secondary" outline className="me-2" onClick={toggle}>
          Cancel
        </Button>
        <Button color="danger" onClick={onConfirm} disabled={loading}>
          Delete{loading ? "..." : ""}
        </Button>
      </div>
    </ThemeModal>
  )
}

const ImporterBankAccountsCard = ({
  importer,
  loading = false,
  onChanged = null,
}) => {
  const importerId = importer?.id
  const accounts = useMemo(
    () => (Array.isArray(importer?.bank_accounts) ? importer.bank_accounts : []),
    [importer]
  )

  const [formModal, setFormModal] = useState(false)
  const [editing, setEditing] = useState(null)
  const [deleting, setDeleting] = useState(null)

  const { mutateAsync: createMutation, isPending: createLoading } =
    useCreateVehicleImporterBankAccount(importerId)
  const { mutateAsync: editMutation, isPending: editLoading } =
    useEditVehicleImporterBankAccount(importerId)
  const { mutateAsync: deleteMutation, isPending: deleteLoading } =
    useDeleteVehicleImporterBankAccount(importerId)
  const { mutateAsync: setDefaultMutation, isPending: setDefaultLoading } =
    useSetDefaultVehicleImporterBankAccount(importerId)

  const openAdd = () => {
    setEditing(null)
    setFormModal(true)
  }

  const openEdit = account => {
    setEditing(account)
    setFormModal(true)
  }

  const closeForm = () => {
    setFormModal(false)
    setEditing(null)
  }

  const handleSubmit = async values => {
    if (!importerId) return

    const newNumber = String(values.account_number || "").trim()
    if (newNumber) {
      const duplicate = accounts.find(
        acc =>
          String(acc.account_number || "").trim().toLowerCase() ===
            newNumber.toLowerCase() &&
          (!editing || acc.id !== editing.id)
      )
      if (duplicate) {
        toast.error(
          `Account number "${newNumber}" already exists for this importer.`
        )
        return
      }
    }

    const payload = {
      label: values.label?.trim() || null,
      account_name: values.account_name?.trim(),
      account_number: values.account_number?.trim(),
      routing_number: values.routing_number?.trim() || null,
      account_mobile_number: values.account_mobile_number?.trim() || null,
      bank_name: values.bank_name?.trim(),
      branch_name: values.branch_name?.trim() || null,
      is_default: !!values.is_default,
      is_active: values.is_active !== false,
    }

    try {
      const res = editing
        ? await editMutation({ id: editing.id, ...payload })
        : await createMutation(payload)
      toast.success(
        res?.message ??
          `Bank account ${editing ? "updated" : "created"} successfully.`
      )
      closeForm()
      onChanged?.()
    } catch (err) {
      const message =
        err?.response?.data?.message ?? "Failed to save bank account."
      toast.error(message)
    }
  }

  const handleDelete = async () => {
    if (!deleting?.id) return
    try {
      const res = await deleteMutation(deleting.id)
      toast.success(res?.message ?? "Bank account deleted.")
      setDeleting(null)
      onChanged?.()
    } catch (err) {
      const message =
        err?.response?.data?.message ?? "Failed to delete bank account."
      toast.error(message)
    }
  }

  const handleSetDefault = async account => {
    try {
      const res = await setDefaultMutation(account.id)
      toast.success(res?.message ?? "Default bank account updated.")
      onChanged?.()
    } catch (err) {
      const message =
        err?.response?.data?.message ?? "Failed to set default bank account."
      toast.error(message)
    }
  }

  const headerAction = useCallback(
    () => (
      <CanView access={[ACCESS.IMPORTER_BANK_ACCOUNT.ADD]}>
        <Button color="primary" size="sm" onClick={openAdd} disabled={!importerId}>
          <i className="bx bx-plus me-1" />
          Add Bank Account
        </Button>
      </CanView>
    ),
    [importerId]
  )

  if (loading) {
    return (
      <Card className="border-0 shadow-sm mb-4">
        <CardBody className="p-3">
          <SectionHeader
            icon="bx bx-bank"
            title="Importer Bank Accounts"
            action={headerAction()}
          />
          <div className="py-2">
            <ThemePlaceholder
              style={{ width: "100%", height: 120, borderRadius: 4 }}
            />
          </div>
        </CardBody>
      </Card>
    )
  }

  return (
    <>
      <Card className="border-0 shadow-sm mb-4">
        <CardBody className="p-3">
          <SectionHeader
            icon="bx bx-bank"
            title={`Bank Accounts (${accounts.length})`}
            action={headerAction()}
          />

          {accounts.length > 0 ? (
            <div className="specs-list">
              {accounts.map((account, index) => (
                <AccountItem
                  key={account.id || index}
                  account={account}
                  index={index}
                  onEdit={openEdit}
                  onDelete={setDeleting}
                  onSetDefault={handleSetDefault}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-3">
              <i className="bx bx-bank display-4 text-muted mb-2"></i>
              <p className="text-muted mb-0">No bank accounts added yet</p>
            </div>
          )}

          {setDefaultLoading ? (
            <div className="small text-muted mt-2">Updating default…</div>
          ) : null}
        </CardBody>
      </Card>

      <ImporterBankAccountAddEdit
        modal={formModal}
        toggleModal={closeForm}
        isEdit={!!editing}
        editedData={editing}
        onSubmit={handleSubmit}
        loading={createLoading || editLoading}
      />

      <ConfirmDeleteModal
        modal={!!deleting}
        toggle={() => setDeleting(null)}
        account={deleting}
        onConfirm={handleDelete}
        loading={deleteLoading}
      />
    </>
  )
}

export default ImporterBankAccountsCard
